import type {
  Alert,
  CARIHistory,
  CARIScore,
  Credential,
  Department,
  FleetCredentialOps,
  FleetCredentialOpsSummary,
  FleetCredentialWorker,
  FleetDashboard,
  FleetSummary,
  FleetWorkerStatus,
  PilotReadiness,
  ShadowObservation,
  User,
  Worker,
} from '@/types';

function normalizeBoolean(value: any): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === '') return false;
  }
  return Boolean(value);
}

function normalizeText(value: any): string | null {
  if (value == null) return null;
  const text = String(value)
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim()
    .replace(/\s+/g, ' ');
  return text || null;
}

export function normalizeWorker(raw: any): Worker {
  return {
    id: raw.id,
    organizationId: raw.organizationId ?? raw.organization_id,
    workerCode: raw.workerCode ?? raw.worker_code,
    department: normalizeText(raw.department),
    jobTitle: normalizeText(raw.jobTitle ?? raw.job_title),
    shiftStart: raw.shiftStart ?? raw.shift_start ?? null,
    shiftEnd: raw.shiftEnd ?? raw.shift_end ?? null,
    deviceId: raw.deviceId ?? raw.device_id ?? null,
    industryProfile: raw.industryProfile ?? raw.industry_profile ?? null,
    baselineHr: raw.baselineHr ?? raw.baseline_hr ?? null,
    baselineHrv: raw.baselineHrv ?? raw.baseline_hrv ?? null,
    currentCari: raw.currentCari ?? raw.current_cari ?? null,
    currentClassification: raw.currentClassification ?? raw.current_classification ?? null,
    lastReadingAt: raw.lastReadingAt ?? raw.last_reading_at ?? null,
    isOnShift: normalizeBoolean(raw.isOnShift ?? raw.is_on_shift),
    isActive: normalizeBoolean(raw.isActive ?? raw.is_active),
    createdAt: raw.createdAt ?? raw.created_at,
  };
}

export function normalizeCARIScore(raw: any): CARIScore {
  return {
    id: raw.id,
    workerId: raw.workerId ?? raw.worker_id,
    score: raw.score ?? 0,
    classification: raw.classification,
    confidence: raw.confidence ?? 0,
    components: {
      hrvComponent: raw.components?.hrvComponent ?? raw.components?.hrv_component ?? 0,
      hrComponent: raw.components?.hrComponent ?? raw.components?.hr_component ?? 0,
      movementComponent:
        raw.components?.movementComponent ?? raw.components?.movement_component ?? 0,
      sleepComponent: raw.components?.sleepComponent ?? raw.components?.sleep_component ?? 0,
    },
    trend: raw.trend ?? null,
    modelVersion: raw.modelVersion ?? raw.model_version ?? null,
    cariV3: raw.cariV3 ?? raw.cari_v3 ?? null,
    timestamp: raw.timestamp,
  };
}

export function normalizeCARIHistory(raw: any): CARIHistory {
  return {
    scores: (raw.scores ?? []).map(normalizeCARIScore),
    periodStart: raw.periodStart ?? raw.period_start,
    periodEnd: raw.periodEnd ?? raw.period_end,
    averageScore: raw.averageScore ?? raw.average_score ?? 0,
    minScore: raw.minScore ?? raw.min_score ?? 0,
    maxScore: raw.maxScore ?? raw.max_score ?? 0,
    timeInGreen: raw.timeInGreen ?? raw.time_in_green ?? 0,
    timeInYellow: raw.timeInYellow ?? raw.time_in_yellow ?? 0,
    timeInRed: raw.timeInRed ?? raw.time_in_red ?? 0,
  };
}

export function normalizeCredential(raw: any): Credential {
  return {
    id: raw.id,
    workerId: raw.workerId ?? raw.worker_id,
    band: raw.band ?? null,
    credentialStatus: raw.credentialStatus ?? raw.credential_status,
    validFrom: raw.validFrom ?? raw.valid_from,
    validUntil: raw.validUntil ?? raw.valid_until,
    bandingConfigVersion: raw.bandingConfigVersion ?? raw.banding_config_version,
    isShadow: normalizeBoolean(raw.isShadow ?? raw.is_shadow),
    createdAt: raw.createdAt ?? raw.created_at,
  };
}

export function normalizeCredentialWorker(raw: any): FleetCredentialWorker {
  return {
    workerId: raw.workerId ?? raw.worker_id,
    workerCode: raw.workerCode ?? raw.worker_code,
    department: normalizeText(raw.department),
    isOnShift: normalizeBoolean(raw.isOnShift ?? raw.is_on_shift),
    currentCari: raw.currentCari ?? raw.current_cari ?? null,
    currentClassification: raw.currentClassification ?? raw.current_classification ?? null,
    latestCredential:
      raw.latestCredential || raw.latest_credential
        ? normalizeCredential(raw.latestCredential ?? raw.latest_credential)
        : null,
    stability: {
      bandFlips24h: raw.stability?.bandFlips24h ?? raw.stability?.band_flips_24h ?? 0,
      timeInBand24h: raw.stability?.timeInBand24h ?? raw.stability?.time_in_band_24h ?? {},
    },
  };
}

export function normalizeShadowObservation(raw: any): ShadowObservation {
  return {
    id: raw.id,
    workerId: raw.workerId ?? raw.worker_id,
    workerCode: raw.workerCode ?? raw.worker_code,
    observationType: raw.observationType ?? raw.observation_type,
    band: raw.band ?? null,
    credentialStatus: raw.credentialStatus ?? raw.credential_status ?? null,
    triggerScore: raw.triggerScore ?? raw.trigger_score ?? null,
    baselineState: raw.baselineState ?? raw.baseline_state ?? null,
    interventionPayload: raw.interventionPayload ?? raw.intervention_payload ?? null,
    metadataJson: raw.metadataJson ?? raw.metadata_json ?? null,
    createdAt: raw.createdAt ?? raw.created_at,
  };
}

export function normalizeCredentialOpsSummary(raw: any): FleetCredentialOpsSummary {
  return {
    shadowModeEnabled: normalizeBoolean(raw.shadowModeEnabled ?? raw.shadow_mode_enabled),
    totalWorkers: raw.totalWorkers ?? raw.total_workers ?? 0,
    credentialedWorkers: raw.credentialedWorkers ?? raw.credentialed_workers ?? 0,
    bandCounts: raw.bandCounts ?? raw.band_counts ?? {},
    statusCounts: raw.statusCounts ?? raw.status_counts ?? {},
    thresholdHitRate24h: raw.thresholdHitRate24h ?? raw.threshold_hit_rate_24h ?? 0,
    baselineCompleteWorkers: raw.baselineCompleteWorkers ?? raw.baseline_complete_workers ?? 0,
    provisionalWorkers: raw.provisionalWorkers ?? raw.provisional_workers ?? 0,
    shadowObservations24h: raw.shadowObservations24h ?? raw.shadow_observations_24h ?? 0,
  };
}

export function normalizeCredentialOps(raw: any): FleetCredentialOps {
  return {
    summary: normalizeCredentialOpsSummary(raw.summary ?? {}),
    workers: (raw.workers ?? []).map(normalizeCredentialWorker),
    shadowObservations: (raw.shadowObservations ?? raw.shadow_observations ?? []).map(
      normalizeShadowObservation
    ),
  };
}

export function normalizePilotReadiness(raw: any): PilotReadiness {
  return {
    organizationId: raw.organizationId ?? raw.organization_id,
    generatedAt: raw.generatedAt ?? raw.generated_at,
    shadowModeEnabled: normalizeBoolean(raw.shadowModeEnabled ?? raw.shadow_mode_enabled),
    overallStatus: raw.overallStatus ?? raw.overall_status ?? 'SHADOW_COLLECTION',
    readyToActivate: normalizeBoolean(raw.readyToActivate ?? raw.ready_to_activate),
    metrics: raw.metrics ?? {},
    checks: raw.checks ?? [],
    knownGaps: raw.knownGaps ?? raw.known_gaps ?? [],
  };
}

export function normalizeUser(raw: any): User {
  return {
    id: raw.id,
    organizationId: raw.organizationId ?? raw.organization_id,
    email: raw.email,
    firstName: normalizeText(raw.firstName ?? raw.first_name),
    lastName: normalizeText(raw.lastName ?? raw.last_name),
    role: raw.role,
    workerId: raw.workerId ?? raw.worker_id ?? null,
    isActive: normalizeBoolean(raw.isActive ?? raw.is_active),
    lastLoginAt: raw.lastLoginAt ?? raw.last_login_at ?? null,
    createdAt: raw.createdAt ?? raw.created_at,
  };
}

export function normalizeAlert(raw: any): Alert {
  return {
    id: raw.id,
    workerId: raw.workerId ?? raw.worker_id,
    alertType: raw.alertType ?? raw.alert_type,
    priority: raw.priority,
    triggerScore: raw.triggerScore ?? raw.trigger_score ?? 0,
    message: raw.message ?? null,
    recommendation: raw.recommendation ?? null,
    acknowledged: raw.acknowledged ?? false,
    acknowledgedAt: raw.acknowledgedAt ?? raw.acknowledged_at ?? null,
    escalated: raw.escalated ?? false,
    resolved: raw.resolved ?? false,
    createdAt: raw.createdAt ?? raw.created_at,
  };
}

export function normalizeFleetSummary(raw: any): FleetSummary {
  return {
    organizationId: raw.organizationId ?? raw.organization_id,
    totalWorkers: raw.totalWorkers ?? raw.total_workers ?? 0,
    activeWorkers: raw.activeWorkers ?? raw.active_workers ?? 0,
    onShiftWorkers: raw.onShiftWorkers ?? raw.on_shift_workers ?? 0,
    greenCount: raw.greenCount ?? raw.green_count ?? 0,
    yellowCount: raw.yellowCount ?? raw.yellow_count ?? 0,
    redCount: raw.redCount ?? raw.red_count ?? 0,
    averageCari: raw.averageCari ?? raw.average_cari ?? 0,
    activeAlerts: raw.activeAlerts ?? raw.active_alerts ?? 0,
    confidenceDistribution:
      raw.confidenceDistribution ?? raw.confidence_distribution ?? null,
    wcFlagSummary: raw.wcFlagSummary ?? raw.wc_flag_summary ?? null,
    timestamp: raw.timestamp,
  };
}

export function normalizeFleetWorkerStatus(raw: any): FleetWorkerStatus {
  return {
    workerId: raw.workerId ?? raw.worker_id ?? raw.id,
    workerCode: raw.workerCode ?? raw.worker_code,
    department: normalizeText(raw.department),
    currentCari: raw.currentCari ?? raw.current_cari ?? null,
    classification: raw.classification ?? raw.current_classification ?? null,
    isOnShift: normalizeBoolean(raw.isOnShift ?? raw.is_on_shift),
    lastReadingAt: raw.lastReadingAt ?? raw.last_reading_at ?? null,
    activeAlerts: raw.activeAlerts ?? raw.active_alerts ?? 0,
  };
}

export function normalizeDepartment(raw: any): Department {
  return {
    department: raw.department,
    totalWorkers: raw.totalWorkers ?? raw.total_workers ?? 0,
    onShiftWorkers: raw.onShiftWorkers ?? raw.on_shift_workers ?? 0,
    averageCari: raw.averageCari ?? raw.average_cari ?? null,
  };
}

export function normalizeFleetDashboard(raw: any): FleetDashboard {
  const summary = normalizeFleetSummary(raw.summary ?? raw.fleet_summary ?? {});
  const workersRaw = raw.workers ?? raw.fleet_workers ?? [];
  const alertsRaw = raw.recentAlerts ?? raw.recent_alerts ?? raw.alerts ?? [];

  return {
    summary,
    workers: workersRaw.map(normalizeFleetWorkerStatus),
    recentAlerts: alertsRaw.map(normalizeAlert),
  };
}
